import { useState } from "react";
import { Box, Text, Button, Modal } from "@/ui";
import { TextField } from "@mui/material";
import { useTheme } from "@mui/material";
import { fetchUpdateBirth } from "@/api/billing/fetchUpdateBirth";
import { useSystemContext } from "@/provider/common/SystemProvider";
import toast from "react-hot-toast";
import { isValid } from "date-fns";

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CompleteProfileModal({ open, onClose }: Props) {
  const theme = useTheme();
  const { getUser, setUser } = useSystemContext();
  const [birth, setBirthDate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const user = getUser();
  console.log("=> ~ user:", user);

  const handleBirthDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBirthDate(event.target.value);
  };

  const handleValidate = async () => {
    if (!birth) {
      toast.error("Veuillez entrer votre date de naissance");
      return;
    }

    try {
      setLoading(true);
      const user = getUser();
      const email = user?.email || null;
      const username = user?.identifiant || null;

      const res = await fetchUpdateBirth({ birth, username, email });

      // Mettre à jour l'utilisateur dans le contexte
      const currentUser = getUser();
      if (currentUser) {
        const updatedUser = { ...currentUser, birth: new Date(birth) };
        setUser(JSON.stringify(updatedUser));
      }

      toast.success("Date de naissance enregistrée avec succès");
      onClose();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement de la date de naissance");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleValidate();
    }
  };
  const firstName = user?.firstName || user?.name || "";
  const lastName = user?.lastname || user?.lastName || "";
  const fullName = `${firstName} ${lastName}`;
  // Vérification que l'année de naissance est supérieure à 1950
  const isYearAbove1950 = () => {
    if (!birth) return false;
    const year = new Date(birth).getFullYear();
    return year > 1940;
  };

  const isBirthDateValid = birth && isValid(new Date(birth)) && new Date(birth) < new Date() && isYearAbove1950();
  
  return (
    <Modal open={open}>
      <Box flexDirection="column" alignItems="center" p={5} width="100%" maxWidth="800px">
        <Box flexDirection="column" alignItems="center" mb={3}>
          <Text fontSize={28} fontWeight="700" color={theme.palette.primary.main} textAlign="center" mb={2}>
            Compléter votre profil
          </Text>
          <Text fontSize={16} fontWeight="400" color={theme.palette.grey[600]} textAlign="center" mb={2}>
            Vérifiez vos informations et complétez votre date de naissance
          </Text>
        </Box>

        <Box flexDirection="column" width="100%" mb={3} gap={3}>
          <TextField
            label="Nom / Prénom"
            value={fullName}
            disabled
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.grey[100],
                },
              },
            }}
          />

          <TextField
            label="Email"
            value={user?.email || ""}
            disabled
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.grey[100],
                },
              },
            }}
          />

          {user?.identifiant && (
            <TextField
              label="Identifiant"
              value={user.identifiant}
              disabled
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.grey[100],
                  },
                },
              }}
            />
          )}

          <TextField
            label="Date de naissance"
            type="date"
            value={birth}
            onChange={handleBirthDateChange}
            onKeyDown={handleKeyDown}
            inputProps={{
              max: new Date().toISOString().split("T")[0],
              style: { textAlign: "center", fontSize: "18px", fontWeight: "600" },
            }}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                "&:hover fieldset": {
                  borderColor: theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
            autoFocus
          />
        </Box>

        <Box flexDirection="row" justifyContent="center" gap={2} width="100%">
          <Button
            onClick={handleValidate}
            disabled={loading || !birth || !isBirthDateValid}
            style={{
              backgroundColor: theme.palette.primary.main,
              color: theme.colors.white,
              fontWeight: "700",
              padding: "10px 30px",
              fontSize: "16px",
              minWidth: "150px",
            }}
            tabIndex={0}
            aria-label="Valider la date de naissance"
          >
            {loading ? "Enregistrement..." : "Valider"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
